import { Injectable } from '@angular/core';
import { EntityKeyByReference, GlobalEntityDefinition } from '@card-software/library';
import { Observable, map } from 'rxjs';
import { MaterialIssue, StatusMaterialIssue } from '../../models/inventory/material-issue';
import { ExternalRequisition } from '../../models/order/external-requisition/external-requisition';
import { SupplyWarehouseDocumentType } from '../../models/supply-warehouse/supply-warehouse-document-type.enum';
import { TableColumn } from '../../models/supply-warehouse/table-column.model';
import { TransactionAction } from '../../models/supply-warehouse/transaction-action.model';
import { MaterialIssueProxyService } from '../../proxies/inventory/material-issue.proxy.service';
import { ExternalRequisitionProxyService } from '../../proxies/order/external-requisition.proxy.service';
import { BaseTransactionStrategy } from './base-transaction.strategy';

/**
 * Estrategia para manejar Material Issues (MI)
 * Documenta el despacho de materiales desde External Requisitions
 */
@Injectable({
  providedIn: 'root',
})
export class MaterialIssueStrategy extends BaseTransactionStrategy<MaterialIssue, StatusMaterialIssue> {
  readonly documentType = SupplyWarehouseDocumentType.MaterialIssue;
  readonly initiatorName = 'External Requisition';
  readonly locationAssignmentType = 'source' as const;

  constructor(
    private materialIssueService: MaterialIssueProxyService,
    private externalRequisitionService: ExternalRequisitionProxyService,
  ) {
    super();
  }

  // ==================== Métodos de Carga ====================

  loadDocument(id: string): Observable<MaterialIssue | null> {
    return this.materialIssueService.getById(id).pipe(map((response) => response.data || null));
  }

  loadInitiatorDocument(externalRequisitionId: string): Observable<ExternalRequisition | null> {
    return this.externalRequisitionService.getById(externalRequisitionId).pipe(map((response) => response.data || null));
  }

  searchTransactionDocuments(externalRequisitionId: string): Observable<MaterialIssue[]> {
    const entityKey = this.getInitiatorEntityKey(externalRequisitionId);
    return this.materialIssueService.search({ initiator: entityKey }).pipe(map((response) => response.data || []));
  }

  createFromInitiator(externalRequisitionId: string, locationCode: string): Observable<MaterialIssue> {
    const entityKey = this.getInitiatorEntityKey(externalRequisitionId);

    // Crear nuevo Material Issue desde External Requisition con locationCode
    return this.materialIssueService.createFromEntityKey(entityKey, locationCode).pipe(map((response) => response.data!));
  }

  // ==================== Métodos de Actualización ====================

  updateDocument(id: string, document: MaterialIssue): Observable<MaterialIssue> {
    return this.materialIssueService.update(id, document).pipe(map((response) => response.data!));
  }

  updateStatus(id: string, newStatus: StatusMaterialIssue): Observable<MaterialIssue> {
    // Usar el método específico de updateStatus
    return this.materialIssueService.updateStatus(id, newStatus).pipe(map((response) => response.data!));
  }

  // ==================== Configuración de UI ====================

  getTableColumns(): TableColumn[] {
    return [
      {
        field: 'itemCode',
        header: 'Item Code',
        width: '150px',
        type: 'text',
        sortable: true,
      },
      { field: 'itemDescription', header: 'Item Description', width: '250px', type: 'text' },
      {
        field: 'quantity',
        header: 'MI Quantity',
        width: '120px',
        type: 'numeric',
        editable: true,
      },
      {
        field: 'uom',
        header: 'UOM',
        width: '100px',
        type: 'text',
      },
      {
        field: 'sourceLocations',
        header: 'Source Locations',
        type: 'locations',
        editable: true,
      },
      {
        field: 'comments',
        header: 'MI Comments',
        width: '200px',
        type: 'text',
        editable: true,
      },
      // {
      //   field: 'actions',
      //   header: 'Common Actions',
      //   width: '100px',
      //   type: 'actions'
      // }
    ];
  }

  getInitiatorItemsColumns(): TableColumn[] {
    return [
      {
        field: 'itemCode',
        header: 'Item Code',
        width: '150px',
        sortable: true,
      },
      {
        field: 'itemDisplayName',
        header: 'Item Description',
        width: '250px',
      },
      {
        field: 'quantity',
        header: 'Requested Qty',
        width: '120px',
        type: 'numeric',
      },
      {
        field: 'issuedQuantity',
        header: 'Issued Qty',
        width: '120px',
        type: 'numeric',
      },
      {
        field: 'pendingQuantity',
        header: 'Pending Qty',
        width: '120px',
        type: 'numeric',
      },
      // {
      //   field: 'actions',
      //   header: 'Common Actions',
      //   width: '100px',
      //   type: 'actions'
      // }
    ];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getAvailableActions(status: StatusMaterialIssue, document: MaterialIssue): TransactionAction[] {
    const actions: TransactionAction[] = [];

    switch (status) {
      case StatusMaterialIssue.Pending:
        actions.push({
          id: 'start-processing',
          label: 'Start Processing',
          icon: 'pi pi-play',
          severity: 'success',
          requiresConfirmation: true,
          confirmationMessage: 'Are you sure you want to start processing this Material Issue?',
        });
        actions.push({
          id: 'cancel',
          label: 'Cancel',
          icon: 'pi pi-times',
          severity: 'danger',
          requiresConfirmation: true,
          confirmationMessage: 'Are you sure you want to cancel this Material Issue?',
        });
        break;

      case StatusMaterialIssue.Processing:
        actions.push({
          id: 'complete',
          label: 'Complete',
          icon: 'pi pi-check',
          severity: 'success',
          requiresConfirmation: true,
          confirmationMessage: 'Are you sure you want to complete this Material Issue? This will create an inventory transaction.',
        });
        // actions.push({
        //   id: 'back-to-pending',
        //   label: 'Back to Pending',
        //   icon: 'pi pi-refresh',
        //   severity: 'warn'
        // });
        break;

      case StatusMaterialIssue.Completed:
        // Solo visualización, no acciones de edición
        break;

      case StatusMaterialIssue.Cancelled:
        // actions.push({
        //   id: 'reopen',
        //   label: 'Reopen',
        //   icon: 'pi pi-replay',
        //   severity: 'warn',
        //   requiresConfirmation: true,
        //   confirmationMessage: 'Are you sure you want to reopen this Material Issue?'
        // });
        break;
    }

    return actions;
  }

  // ==================== Reglas de Negocio ====================

  canEditQuantities(status: StatusMaterialIssue): boolean {
    return status === StatusMaterialIssue.Pending || status === StatusMaterialIssue.Processing;
  }

  canEditLines(status: StatusMaterialIssue): boolean {
    return status === StatusMaterialIssue.Pending || status === StatusMaterialIssue.Processing;
  }

  validateForStatusChange(document: MaterialIssue, newStatus: StatusMaterialIssue): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validar que tenga líneas
    if (!document.lines || document.lines.length === 0) {
      errors.push('Material Issue must have at least one line');
    }

    // Validar que todas las líneas tengan cantidad > 0
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    document.lines?.forEach((line: any, index: number) => {
      if (line.quantity <= 0) {
        errors.push(`MI_Error_Invalid_Quantity_Line_${index + 1}`);
      }
    });

    // Si cambia a Processing o Completed, validar ubicaciones de origen
    if (newStatus === StatusMaterialIssue.Processing || newStatus === StatusMaterialIssue.Completed) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      document.lines?.forEach((line: any, index: number) => {
        if (!line.sourceLocations || line.sourceLocations.length === 0) {
          errors.push(`MI_Error_No_Source_Locations_Line_${index + 1}`);
        } else {
          // Validar que la suma de sourceLocations = cantidad
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const totalInLocations = line.sourceLocations.reduce((sum: number, loc: any) => sum + loc.quantity, 0);
          const diff = Math.abs(totalInLocations - line.quantity);
          if (diff > 0.001) {
            // Tolerancia para decimales
            errors.push(`MI_Error_Location_Mismatch_Line_${index + 1}`);
          }
        }
      });
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  // ==================== Helpers ====================

  getDocumentTitle(document: MaterialIssue | null): string {
    if (!document) return 'New Material Issue';
    return document.number || 'Material Issue';
  }

  getInitiatorEntityKey(externalRequisitionId: string): EntityKeyByReference {
    return {
      reference: externalRequisitionId,
      entityDefinitionCode: GlobalEntityDefinition.ExternalRequisition,
    };
  }
}
